import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';

// Where test-mode sends are redirected. Mirrors TEST_RECIPIENT on the
// server (leadsController.js). Purely informational here.
const TEST_DESTINATION = 'david@xsitegroup.ca';

// Render any template string (subject OR body) against a contact record
// + the logged-in user's profile. Lead-side merge tags surface contact
// fields; {{user_*}} tags surface the logged-in user (name, title,
// email) for the closing signature block. Missing values render as the
// literal placeholder so it's obvious which one didn't substitute.
// Renamed from renderBody → renderTemplate 2026-05-24 when we started
// using it on the subject line too (previously only the body was being
// rendered, so subjects like "Bulloch back-office for {{company}}"
// were sent with the literal placeholder still in them).
// siteListBlock pre-rendered as a bulleted multi-line string (one site
// per line, "  • Site Name"). Empty string when no sites available.
// Server-side substitution in customersController.sendEmail mirrors
// this logic, so the preview and the actually-sent email match.
function renderTemplate(text, record, user, siteListBlock = '', siteContext = null) {
  if (!text) return '';
  // siteContext is set when the email is about ONE specific site
  // (e.g. "Send Email" from a Centex site row). Site-specific tokens
  // resolve against that single site; without a siteContext they
  // stay as placeholders so the sender notices.
  const siteName     = siteContext?.site_name         || '';
  const siteNumber   = siteContext?.site_number       || '';
  const siteAddress  = siteContext?.address           || '';
  const siteCity     = siteContext?.city              || '';
  const siteProvince = siteContext?.province_state    || '';
  // xsiteio credentials baked into the site row -- used by the
  // onboarding-welcome template so a new customer gets their portal
  // login in the same email that confirms their setup. Dave 2026-06-24.
  const xsiteioUser  = siteContext?.xsite_io_username || '';
  const xsiteioPass  = siteContext?.xsite_io_password || '';
  return text
    .replace(/\{\{site_name\}\}/g,        siteName     || '{{site_name}}')
    .replace(/\{\{site_number\}\}/g,      siteNumber   || '{{site_number}}')
    .replace(/\{\{site_address\}\}/g,     siteAddress  || '{{site_address}}')
    .replace(/\{\{site_city\}\}/g,        siteCity     || '{{site_city}}')
    .replace(/\{\{site_province\}\}/g,    siteProvince || '{{site_province}}')
    .replace(/\{\{xsiteio_username\}\}/g, xsiteioUser  || '{{xsiteio_username}}')
    .replace(/\{\{xsiteio_password\}\}/g, xsiteioPass  || '{{xsiteio_password}}')
    .replace(/\{\{first_name\}\}/g, record?.first_name || '{{first_name}}')
    .replace(/\{\{last_name\}\}/g,  record?.last_name  || '{{last_name}}')
    .replace(/\{\{company\}\}/g,    record?.company    || '{{company}}')
    .replace(/\{\{position\}\}/g,   record?.position   || '{{position}}')
    .replace(/\{\{city\}\}/g,       record?.city       || '{{city}}')
    .replace(/\{\{state\}\}/g,      record?.state      || record?.province_state || '{{state}}')
    // Extra fields used by the PetroCan back-office template
    // (Dave 2026-06-12). Site-info table needs the full address +
    // postal + both phones + email + today's date.
    .replace(/\{\{address1\}\}/g,   record?.address1   || '{{address1}}')
    .replace(/\{\{postal_zip\}\}/g, record?.postal_zip || '{{postal_zip}}')
    .replace(/\{\{phone_main\}\}/g, record?.phone_main || '{{phone_main}}')
    .replace(/\{\{phone_cell\}\}/g, record?.phone_cell || '{{phone_cell}}')
    .replace(/\{\{email\}\}/g,      record?.email1     || record?.email || '{{email}}')
    .replace(/\{\{contact_name\}\}/g, ((record?.first_name || '') + ' ' + (record?.last_name || '')).trim() || '{{contact_name}}')
    .replace(/\{\{today\}\}/g,      new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }))
    // {{site_list}} -- customer-only (leads/distributors don't have
    // sites). When sites are loaded, renders as bulleted lines; when
    // not (no sites on file), leaves the placeholder visible so it's
    // obvious the merge didn't fire.
    .replace(/\{\{site_list\}\}/g, siteListBlock || '{{site_list}}')
    // Distributor-specific pitch tokens. Substitute only when the record
    // has a value; otherwise leave the placeholder literal so it stays
    // visible to the sender as a reminder to fill the field in.
    .replace(/\{\{team_function\}\}/g,  record?.team_function  || '{{team_function}}')
    .replace(/\{\{peer_reference\}\}/g, record?.peer_reference || '{{peer_reference}}')
    .replace(/\{\{their_function\}\}/g, record?.their_function || '{{their_function}}')
    .replace(/\{\{user_name\}\}/g,  user?.name  || '{{user_name}}')
    .replace(/\{\{user_title\}\}/g, user?.title || '{{user_title}}')
    .replace(/\{\{user_email\}\}/g, user?.email || '{{user_email}}')
    // Calendly booking link for the logged-in rep. {{owner_calendly_url}}
    // is the preferred token (matches the lead's "owner"); {{user_calendly_url}}
    // is the same value under a more obvious alias. Leaves the placeholder
    // visible when no URL set, so the sender remembers to update Settings.
    .replace(/\{\{owner_calendly_url\}\}/g, user?.calendly_url || '{{owner_calendly_url}}')
    .replace(/\{\{user_calendly_url\}\}/g,  user?.calendly_url || '{{user_calendly_url}}');
}

// Last-quote merge fields for follow-up templates (Dave 2026-10-26).
// q = { date: 'YYYY-MM-DD' | null } from /api/leads/:id/last-quote; the
// server's batch send does the same substitution (leadsController).
function quoteValues(q) {
  const iso = q?.date;
  if (!iso) return { quote_ref: 'the quote I sent over', last_quote_month: '', last_quote_date: '' };
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const month = dt.toLocaleDateString('en-CA', { month: 'long' });
  const monthPhrase = y === new Date().getFullYear() ? month : `${month} ${y}`;
  return {
    quote_ref: `the quote I sent over in ${monthPhrase}`,
    last_quote_month: monthPhrase,
    last_quote_date: dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }),
  };
}
function applyQuote(text, q) {
  if (!text || q === undefined) return text; // not a lead / not loaded: leave tokens visible
  const v = quoteValues(q);
  return String(text).replace(/\{\{(quote_ref|last_quote_month|last_quote_date)\}\}/g, (_, k) => v[k]);
}

// Fuel-brand -> CC group mapping. Kept in sync with the server-side
// bullochController.js PARKLAND_BRANDS and SupportTeamPanel.jsx. Dave's
// 2026-07-09 policy: PetroCan/Suncor -> Jim (petrocan_jim group),
// Chevron/Fast Gas/Ultramar/Pioneer -> parkland, everything else ->
// bulloch_only.
// Matched case- and space-insensitively so 'Fast Gas', 'FastGas' and
// 'FasGas' all resolve to Parkland -- a spelling variant would otherwise
// fall through to bulloch_only.
function isParklandBrand(fuelBrand) {
  const s = String(fuelBrand || '').toLowerCase().replace(/\s+/g, '');
  return s.includes('chevron') || s.includes('ultramar') || s.includes('pioneer')
      || s.includes('fasgas')  || s.includes('fastgas');
}
function isPetroCanBrand(fuelBrand) {
  const lc = String(fuelBrand || '').toLowerCase();
  return lc.includes('petro canada') || lc.includes('petrocan') || lc.includes('suncor');
}
function groupForFuelBrand(fuelBrand) {
  if (isPetroCanBrand(fuelBrand))     return 'petrocan_jim';
  if (isParklandBrand(fuelBrand))     return 'parkland';
  return 'bulloch_only';
}

// Merge two comma-separated email strings, deduping (case-insensitive)
// and dropping empties. Preserves the original order so template CCs
// stay in front and group CCs follow.
function mergeCcs(...parts) {
  const seen = new Set();
  const out = [];
  for (const part of parts) {
    if (!part) continue;
    for (const raw of String(part).split(/[,;]/)) {
      const trimmed = raw.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(trimmed);
    }
  }
  return out.join(', ');
}

// Two-step flow:
//   1. compose → pick template, see prefilled subject/body, edit, send
//   2. sent    → confirmation summary with Resend message id
//
// Test Mode defaults ON (same safety convention as the Quote / Bulloch
// / Statement / Price-Increase dialogs). LIVE is a deliberate toggle.
export default function SendEmailDialog({ open, record, onClose, onSent, recordType = 'lead', sites = null, siteContext = null }) {
  // Pre-render {{site_list}} from the sites array + customer's
  // last_contact (rough cancellation proxy). Each line ends in a
  // suffix like "(with us 2019–2024)" or "(with us since 2019)"
  // depending on what data we have. Server-side substitution mirrors
  // this exactly so the preview matches the sent email.
  const siteListBlock = (() => {
    if (!Array.isArray(sites) || sites.length === 0) return '';
    const endYear = record?.last_contact
      ? new Date(record.last_contact).getFullYear()
      : null;
    const suffix = (installYear) => {
      if (installYear && endYear && endYear > installYear) return ` (with us ${installYear}–${endYear})`;
      if (installYear)                                     return ` (with us since ${installYear})`;
      return '';
    };
    const sorted = [...sites].sort((a, b) => {
      const aActive = (a.status || 'Active') === 'Active' ? 0 : 1;
      const bActive = (b.status || 'Active') === 'Active' ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return (a.site_name || '').localeCompare(b.site_name || '');
    });
    return sorted
      .filter((s) => s.site_name)
      .map((s) => {
        const yr = s.install_date ? new Date(s.install_date).getFullYear() : null;
        return `  • ${s.site_name}${suffix(yr)}`;
      })
      .join('\n');
  })();
  const { user } = useAuth();
  const [step, setStep] = useState('compose');
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipient, setRecipient] = useState('');
  // CC list -- comma-separated string, populated from the selected
  // template's `cc` column PLUS the site's fuel_brand support group
  // (Dave 2026-07-09 policy: PetroCan -> Jim, Chevron/Fast Gas/Ultramar/
  // Pioneer -> parkland, everything else -> bulloch_only). Editable in
  // the dialog so the sender can prune/add before sending.
  const [ccList, setCcList] = useState('');
  // Cache of the fuel-brand support-group emails, populated once when
  // the dialog opens with a siteContext. Fed into selectTemplate so
  // switching templates re-applies the group CCs.
  const [siteGroupCcs, setSiteGroupCcs] = useState('');
  // Most recent quote for a lead: undefined = not a lead / not loaded,
  // { date: null } = none on file. Ref so selectTemplate sees it at once.
  const [lastQuote, setLastQuote] = useState(undefined);
  const lastQuoteRef = useRef(undefined);
  const [testMode, setTestMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Initial load: templates + recipient prefill + site fuel-brand
  // support group on every open. Templates and the CC group are
  // fetched in parallel so the first-template auto-pick can merge the
  // group CCs immediately (no flash of empty CC field).
  useEffect(() => {
    if (!open) return;
    setStep('compose');
    setError(null);
    setResult(null);
    setSending(false);
    setTestMode(true);
    setRecipient(record?.email1 || '');
    setSiteGroupCcs('');   // reset from previous open in case siteContext changed
    setLastQuote(undefined);
    lastQuoteRef.current = undefined;
    setLoading(true);

    // Determine the CC group for this send. Prefer the site's fuel_brand
    // (per-site accuracy) and fall back to the record's fuel_brand when
    // no siteContext was passed. Customer-level sends on multi-brand
    // customers therefore use the customer's primary brand -- imperfect
    // but consistent with the per-site rule Dave set on 2026-07-09.
    // Sales emails (leads) never get the Bulloch/support CC groups -- those
    // are for customer help emails. Leads always CC Mary and the sender
    // instead (the server adds them too). Dave 2026-10-26.
    const fuelBrand = siteContext?.fuel_brand || record?.fuel_brand || '';
    const targetGroup = (recordType !== 'lead' && fuelBrand) ? groupForFuelBrand(fuelBrand) : null;

    const templatesPromise = axios.get('/api/email-templates');
    const ccGroupsPromise  = targetGroup
      ? axios.get('/api/cc-groups').catch(() => ({ data: [] }))
      : Promise.resolve({ data: [] });

    const quotePromise = (recordType === 'lead' && record?.id)
      ? axios.get(`/api/leads/${record.id}/last-quote`).then((r) => r.data).catch(() => ({ date: null }))
      : Promise.resolve(undefined);

    Promise.all([templatesPromise, ccGroupsPromise, quotePromise])
      .then(([tplRes, ccRes, quoteRes]) => {
        lastQuoteRef.current = quoteRes;
        setLastQuote(quoteRes);
        // Derive the group CC string first so we can pass it into
        // selectTemplate synchronously (avoids the "first template
        // loads with empty siteGroupCcs" race).
        let groupCcs = '';
        if (recordType === 'lead') {
          // Mary + the sender (their own copy); the server adds both too.
          groupCcs = mergeCcs('mary@xsitegroup.ca', user?.email || '');
          setSiteGroupCcs(groupCcs);
        }
        if (targetGroup && Array.isArray(ccRes.data)) {
          groupCcs = ccRes.data
            .filter((c) => c.group_name === targetGroup && c.active && c.email)
            .map((c) => c.email)
            .join(', ');
          setSiteGroupCcs(groupCcs);
        }

        const list = tplRes.data?.templates || [];
        setTemplates(list);
        if (list.length > 0) {
          // Auto-pick the first template so the user sees the body
          // immediately without an extra click.
          selectTemplate(list[0], record, groupCcs);
        }
      })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record?.id, user?.id, siteContext?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, sending]);

  if (!open) return null;

  function selectTemplate(tpl, rec = record, groupCcsOverride = null) {
    setSelectedId(tpl.id);
    // Render BOTH subject and body — earlier this only rendered the
    // body, so a subject like "Bulloch back-office for {{company}}"
    // came through with the literal placeholder.
    setSubject(applyQuote(renderTemplate(tpl.subject, rec, user, siteListBlock, siteContext), lastQuoteRef.current));
    setBody(applyQuote(renderTemplate(tpl.body, rec, user, siteListBlock, siteContext), lastQuoteRef.current));
    // Merge template CC + site fuel-brand support group emails.
    // groupCcsOverride lets the initial-load path pass the freshly-
    // fetched value in without waiting for a state re-render (avoids
    // the "first template loads with an empty siteGroupCcs" race).
    const groupCcs = groupCcsOverride != null ? groupCcsOverride : siteGroupCcs;
    setCcList(mergeCcs(tpl.cc, groupCcs));
  }

  function handleTemplateChange(e) {
    const id = Number(e.target.value);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) selectTemplate(tpl);
  }

  async function handleSend() {
    setError(null);
    if (!recipient.trim()) { setError('Recipient email is required.'); return; }
    if (!subject.trim())   { setError('Subject is required.'); return; }
    if (!body.trim())      { setError('Body is required.'); return; }
    setSending(true);
    try {
      const tpl = templates.find((t) => t.id === selectedId);
      // Pick the per-record-type endpoint. Each record type has its
      // own controller / route — keeps per-type note + document
      // logging localised. Default is the lead endpoint.
      const endpoint =
        recordType === 'distributor'       ? `/api/distributors/${record.id}/send-email` :
        recordType === 'strategic_account' ? `/api/strategic-accounts/${record.id}/send-email` :
        recordType === 'vendor_contact' ? `/api/vendor-contacts/${record.id}/send-email` :
        recordType === 'customer'          ? `/api/customers/${record.id}/send-email`    :
                                             `/api/leads/${record.id}/send-email`;
      const res = await axios.post(endpoint, {
        to: recipient.trim(),
        cc: ccList.trim() || null,
        subject: subject.trim(),
        body,
        template_id: selectedId,
        template_name: tpl?.name || null,
        test_mode: testMode,
        // Synthesized record from a sub-contact carries sub_contact_id
        // so the server can stamp it on the resulting documents row.
        // Falsy when emailing the parent record directly.
        sub_contact_id: record?.sub_contact_id || null
      });
      setResult(res.data);
      setStep('sent');
      if (onSent) onSent(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
    >
      <div className="modal-card modal-wide" role="dialog" aria-modal="true" aria-label="Send Email">
        <header className="modal-head">
          <h3>
            {step === 'compose' && 'Send Email'}
            {step === 'sent'    && (result?.test_mode ? 'Test Email Sent' : 'Email Sent')}
          </h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={sending}
            aria-label="Close"
          >×</button>
        </header>

        {step === 'compose' && (
          <div className="modal-body">
            {loading && <p className="modal-sub">Loading templates…</p>}

            {!loading && templates.length === 0 && (
              <p className="error">No email templates available. Seed at least one via <code>migrate-email-templates.js</code>.</p>
            )}

            {!loading && templates.length > 0 && (
              <>
                <div className="form-grid">
                  <label className="form-field form-field-wide">
                    <span>Template</span>
                    <select value={selectedId || ''} onChange={handleTemplateChange}>
                      {/* Group templates by category via native <optgroup>.
                          The controller already sorts so categories arrive
                          in render order with NULLs last; we just bucket
                          them here. "Uncategorised" only renders when at
                          least one template has a NULL category, so legacy
                          templates aren't hidden during the rollout. */}
                      {Object.entries(
                        templates.reduce((acc, t) => {
                          const key = t.category || 'Uncategorised';
                          (acc[key] = acc[key] || []).push(t);
                          return acc;
                        }, {})
                      ).map(([category, group]) => (
                        <optgroup key={category} label={category}>
                          {group.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                  {lastQuote !== undefined && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: -4 }}>
                      {lastQuote?.date
                        ? <>Last quote: <strong>{new Date(`${lastQuote.date}T12:00:00`).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}</strong></>
                        : 'No quote on file in Command (a quote follow-up will just say "the quote I sent over")'}
                    </div>
                  )}
                  <label className="form-field form-field-wide">
                    <span>To</span>
                    <input
                      type="email"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      className="cell-mono"
                      placeholder="client@example.com"
                    />
                  </label>
                  {/* CC field -- empty by default, populated from
                      the selected template's `cc` column. Editable so
                      the sender can prune/add per-send. Comma- or
                      semicolon-separated. Dave 2026-06-24. */}
                  <label className="form-field form-field-wide">
                    <span>CC</span>
                    <input
                      type="text"
                      value={ccList}
                      onChange={(e) => setCcList(e.target.value)}
                      className="cell-mono"
                      placeholder="dhruval@centexpetroleum.com, optional@..."
                    />
                  </label>
                  <label className="form-field form-field-wide">
                    <span>Subject</span>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </label>
                  <label className="form-field form-field-wide">
                    <span>Body (preview — edit freely)</span>
                    <textarea
                      rows={14}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      className="send-email-body"
                    />
                  </label>
                </div>

                <div className={`test-mode-panel test-mode-${testMode ? 'on' : 'off'}`}>
                  <label className="test-mode-toggle">
                    <input
                      type="checkbox"
                      checked={testMode}
                      onChange={(e) => setTestMode(e.target.checked)}
                      disabled={sending}
                    />
                    <span className="test-mode-label">Test Mode</span>
                  </label>
                  {testMode ? (
                    <p className="test-mode-status">
                      Email will be redirected to <strong>{user?.email || TEST_DESTINATION}</strong> instead of
                      <code> {recipient || '(no recipient)'}</code>. No note logged on the lead record.
                    </p>
                  ) : (
                    <p className="test-mode-status">
                      <strong>LIVE</strong> — email will be sent to <code>{recipient || '(no recipient)'}</code>
                      {' '}and a note will be logged on the lead record.
                    </p>
                  )}
                </div>

                {error && <p className="error">{error}</p>}

                <div className="form-actions" style={{ marginTop: 'var(--space-3)' }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={sending}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSend}
                    disabled={sending || !recipient.trim()}
                  >
                    {sending
                      ? 'Sending…'
                      : (testMode ? 'Send Test Email' : 'Send Email (LIVE)')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 'sent' && result && (
          <div className="modal-body">
            {result.test_mode && (
              <p
                className="error"
                style={{
                  background: 'var(--color-warn-bg)',
                  color: 'var(--color-warn)',
                  borderColor: 'var(--color-warn-border)'
                }}
              >
                <strong>TEST MODE active</strong> — server redirected the email
                from <code>{result.intended_to}</code> to <code>{result.to}</code>.
                No note was logged on the lead record.
                 </p>
            )}
            <div className="quote-success">
              <div className="quote-success-icon">✓</div>
              <h4>{result.test_mode ? 'Test Email Sent' : 'Email Sent'}</h4>
              <p>Sent to <strong>{result.to}</strong>.</p>
              <dl className="quote-success-meta">
                <div><dt>Template</dt><dd>{result.template_name || '—'}</dd></div>
                <div><dt>Subject</dt><dd>{result.subject}</dd></div>
                <div><dt>Resend ID</dt><dd className="cell-mono">{result.resend_message_id || '—'}</dd></div>
                <div><dt>Sent At</dt><dd className="cell-mono">{new Date(result.sent_at).toLocaleString()}</dd></div>
              </dl>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={onClose}>Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
